import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Pagination from '@mui/material/Pagination';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { ApiError, listCustomerDeposits } from './customerCenterApi';
import { displayMoney } from './money';
import type { BranchId, CustomerDeposit, CustomerId, CustomerRow, DepositId } from './types';
import CustomerDepositDetail from './CustomerDepositDetail';

const DEPOSIT_PAGE_SIZE = 20;
const PLACEHOLDER = '--';

function toDisplayDate(value: string | null): string {
  if (!value) return PLACEHOLDER;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : value;
}

function lastPageFor(total: number, perPage: number): number {
  return Math.max(Math.ceil(total / perPage), 1);
}

interface Props {
  show: boolean;
  branchId: BranchId;
  customer: CustomerRow | null;
  onClose: () => void;
}

const CustomerDepositsModal: React.FC<Props> = ({ show, branchId, customer, onClose }) => {
  const { t } = useTranslation('scenes.customerCenter');
  const [deposits, setDeposits] = useState<CustomerDeposit[]>([]);
  const [totalDeposits, setTotalDeposits] = useState(0);
  const [depositPage, setDepositPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);
  const [detailDepositId, setDetailDepositId] = useState<DepositId | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!show || !customer) return;
    setDepositPage(1);
    setDetailDepositId(null);
  }, [show, customer?.id]);

  useEffect(() => {
    if (!show || !customer || !branchId) return;
    seq.current += 1;
    const mySeq = seq.current;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setDenied(false);
    listCustomerDeposits(branchId, customer.id as CustomerId, depositPage, DEPOSIT_PAGE_SIZE, controller.signal).then(
      (result) => {
        if (seq.current !== mySeq) return;
        setDeposits(result.items);
        setTotalDeposits(result.total);
        setLoading(false);
      },
      (err: unknown) => {
        if (seq.current !== mySeq) return;
        setDeposits([]);
        setTotalDeposits(0);
        setLoading(false);
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setDenied(true);
          return;
        }
        const message = err instanceof Error ? err.message : t('failedDeposits');
        setError(message);
        toast.error(message);
      },
    );
    return () => controller.abort();
  }, [show, branchId, customer, depositPage, t]);

  const depositPages = lastPageFor(totalDeposits, DEPOSIT_PAGE_SIZE);

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{customer ? t('linkedDepositsFor', { name: customer.name }) : t('linkedDeposits')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={`page-loader ${loading ? 'visible' : ''}`}><div className="loader" /></div>
          {denied ? <div className="customer-center__error" role="alert">{t('denied')}</div> : null}
          {error ? <div className="customer-center__error" role="alert">{error}</div> : null}
          <div className="table-responsive list-table table-scroll">
            <table className="table table-default">
              <thead>
                <tr className="align-middle">
                  <th scope="col">{t('colDate')}</th>
                  <th scope="col">{t('colDescription')}</th>
                  <th scope="col" className="text-end">{t('colShare')}</th>
                  <th scope="col" className="text-end">{t('colOriginalTotal')}</th>
                  <th scope="col" className="text-end">{t('colDetail')}</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((row) => (
                  <tr key={String(row.id)} className="align-middle h-60">
                    <td>{toDisplayDate(row.date)}</td>
                    <td>{row.description || PLACEHOLDER}</td>
                    <td className="text-end">{displayMoney(row.customer_amount)}</td>
                    <td className="text-end">{displayMoney(row.deposit_amount)}</td>
                    <td className="text-end">
                      <button type="button" className="customer-center__link-btn" onClick={() => setDetailDepositId(row.id)}>
                        {t('open')}
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && deposits.length === 0 && !error && !denied ? (
                  <tr><td colSpan={5} className="text-center">{t('noLinkedDeposits')}</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <Pagination
            count={depositPages}
            page={depositPage}
            showFirstButton
            showLastButton
            onChange={(_e: ChangeEvent<unknown>, value: number) => setDepositPage(value)}
            size="medium"
            disabled={!totalDeposits}
          />
        </Modal.Body>
      </Modal>
      {detailDepositId !== null && customer ? (
        <CustomerDepositDetail
          key={`${branchId}:${String(detailDepositId)}`}
          branchId={branchId}
          depositId={detailDepositId}
          onClose={() => setDetailDepositId(null)}
          onChanged={() => setDepositPage((p) => p)}
        />
      ) : null}
    </>
  );
};

export default CustomerDepositsModal;
